<script lang="ts">
    import { useForm, page } from '@inertiajs/svelte';
    import { router } from '@inertiajs/svelte';
    import { onMount } from 'svelte';
    import MediaLightbox from './MediaLightbox.svelte';
    import MediaUploadList from './MediaUploadList.svelte';
    import Modal from './Modal.svelte';
    import RichTextEditor from './RichTextEditor.svelte';
    import { uploadFile, type UploadItem } from '../services/uploadService';

    interface QuestionOption {
        id?: number;
        body: string;
        is_correct: boolean;
        order: number;
    }

    interface Question {
        id?: number;
        body: string;
        type: 'free_text' | 'multiple_choice';
        correct_answer?: string;
        order: number;
        options?: QuestionOption[];
    }

    interface MediaItem {
        id: number;
        url: string;
        caption: string;
        mime_type: string;
    }

    interface ExistingMedia {
        institution_logo: string | null;
        media: MediaItem[];
        additional_media: MediaItem[];
    }

    interface TopicOption {
        id: number;
        name: string;
        disabled: boolean;
    }

    interface Case {
        id?: number;
        history: string;
        institution?: string;
        authors?: string[];
        topic_id?: number | null;
        discussion?: string;
        differential?: string;
        references?: string[];
        questions?: Question[];
    }

    let {
        case: existingCase = null,
        existingMedia = null,
        computedStatus = null,
        topics = [],
    }: {
        case: Case | null;
        existingMedia: ExistingMedia | null;
        computedStatus: string | null;
        topics: TopicOption[];
    } = $props();

    const tabs = ['Basics', 'Author', 'Media', 'Question', 'Discussion', 'Submit'] as const;
    type Tab = typeof tabs[number];

    function tabFromHash(): Tab {
        const hash = window.location.hash.slice(1);
        return tabs.includes(hash as Tab) ? (hash as Tab) : 'Basics';
    }

    let activeTab = $state<Tab>(tabFromHash());
    let tabNav: HTMLElement;
    let tabIndicatorLeft = $state(0);
    let tabIndicatorWidth = $state(0);

    function updateTabIndicator(): void {
        if (!tabNav) return;
        const active = tabNav.querySelector('[data-active]') as HTMLElement | null;
        if (active) {
            const navRect = tabNav.getBoundingClientRect();
            const activeRect = active.getBoundingClientRect();
            tabIndicatorLeft = activeRect.left - navRect.left;
            tabIndicatorWidth = activeRect.width;
        }
    }

    $effect(() => {
        activeTab;
        updateTabIndicator();
    });

    function setTab(tab: Tab) {
        activeTab = tab;
        history.replaceState(null, '', `#${tab}`);
    }

    function onHashChange() {
        activeTab = tabFromHash();
    }

    const existingQuestion = existingCase?.questions?.[0];

    const defaultAuthors = existingCase?.authors ?? [$page?.props?.auth?.user?.name ?? ''];

    const form = useForm({
        history: existingCase?.history ?? '',
        institution: existingCase?.institution ?? '',
        topic_id: existingCase?.topic_id ?? (null as number | null),
        authors: defaultAuthors.length > 0 ? defaultAuthors : [''],
        discussion: existingCase?.discussion ?? '',
        differential: existingCase?.differential ?? '',
        references: existingCase?.references ?? [],
        question: {
            id: existingQuestion?.id ?? null,
            body: existingQuestion?.body ?? '',
            type: (existingQuestion?.type ?? '') as '' | 'free_text' | 'multiple_choice',
            correct_answer: existingQuestion?.correct_answer ?? '',
            options: existingQuestion?.options ?? [
                { body: '', is_correct: false, order: 0 },
                { body: '', is_correct: false, order: 1 },
            ],
        },
        update_media_captions: Object.fromEntries([
            ...(existingMedia?.media ?? []).map(m => [m.id, m.caption]),
            ...(existingMedia?.additional_media ?? []).map(m => [m.id, m.caption]),
        ]) as Record<number, string>,
        remove_media: [] as number[],
        reorder_media: {} as Record<string, number[]>,
    });

    let logoPreview = $state<string | null>(existingMedia?.institution_logo ?? null);
    let logoUpload = $state<UploadItem | null>(null);

    let mediaUploads = $state<UploadItem[]>([]);
    let additionalMediaUploads = $state<UploadItem[]>([]);

    let uploadCounter = 0;
    function nextUploadId(): string {
        return `upload-${++uploadCounter}`;
    }

    function findUploadItem(id: string): UploadItem | undefined {
        return mediaUploads.find(u => u.id === id)
            ?? additionalMediaUploads.find(u => u.id === id)
            ?? (logoUpload?.id === id ? logoUpload : undefined);
    }

    function startUpload(itemId: string, file: File): void {
        const item = findUploadItem(itemId);
        if (item) item.status = 'uploading';

        const { promise, abort } = uploadFile(file, (percent) => {
            const target = findUploadItem(itemId);
            if (target) target.progress = percent;
        });

        if (item) {
            item.abort = abort;
            item.retry = () => startUpload(itemId, file);
        }

        promise.then((key) => {
            const target = findUploadItem(itemId);
            if (target) {
                target.s3Key = key;
                target.progress = 100;
                target.status = 'done';
            }
        }).catch((err) => {
            if (err?.name === 'AbortError') return;
            const target = findUploadItem(itemId);
            if (target) {
                target.status = 'error';
                target.error = err?.message ?? 'Upload failed';
            }
        });
    }

    function createUploadItems(files: File[]): UploadItem[] {
        return files.map(file => ({
            id: nextUploadId(),
            file,
            s3Key: null,
            progress: 0,
            status: 'pending' as const,
            caption: '',
        }));
    }

    function startUploads(items: UploadItem[]): void {
        for (const item of items) {
            startUpload(item.id, item.file);
        }
    }

    const uploadsInProgress = $derived(
        mediaUploads.some(u => u.status === 'uploading' || u.status === 'pending')
        || additionalMediaUploads.some(u => u.status === 'uploading' || u.status === 'pending')
        || (logoUpload !== null && (logoUpload.status === 'uploading' || logoUpload.status === 'pending'))
    );

    let lightboxOpen = $state(false);
    let lightboxIndex = $state(0);
    let lightboxImages = $state<{ id?: number; src: string; alt?: string; caption?: string }[]>([]);

    function openLightbox(images: { id?: number; src: string; alt?: string; caption?: string }[], index: number) {
        lightboxImages = images;
        lightboxIndex = index;
        lightboxOpen = true;
    }

    function closeLightbox() {
        lightboxOpen = false;
    }

    function handleLightboxCaptionChange(index: number, value: string) {
        const image = lightboxImages[index];
        if (!image?.id) return;
        $form.update_media_captions = { ...$form.update_media_captions, [image.id]: value };
        lightboxImages[index] = { ...image, caption: value };
    }

    let mediaOrder = $state<number[]>((existingMedia?.media ?? []).map(m => m.id));
    let additionalMediaOrder = $state<number[]>((existingMedia?.additional_media ?? []).map(m => m.id));

    const orderedMedia = $derived(() => {
        const items = (existingMedia?.media ?? []);
        return mediaOrder.map(id => items.find(m => m.id === id)).filter((m): m is MediaItem => !!m);
    });

    const orderedAdditionalMedia = $derived(() => {
        const items = (existingMedia?.additional_media ?? []);
        return additionalMediaOrder.map(id => items.find(m => m.id === id)).filter((m): m is MediaItem => !!m);
    });

    const tabErrors: Record<Tab, () => boolean> = {
        Basics: () => !!($form.errors.history || $form.errors.topic_id),
        Author: () => !!($form.errors.institution || $form.errors.institution_logo || Object.keys($form.errors).some(k => k.startsWith('authors'))),
        Media: () => !!(Object.keys($form.errors).some(k => k.startsWith('media') || k.startsWith('remove_media'))),
        Question: () => !!(Object.keys($form.errors).some(k => k.startsWith('question'))),
        Discussion: () => !!($form.errors.discussion || $form.errors.differential || Object.keys($form.errors).some(k => k.startsWith('references') || k.startsWith('additional_media'))),
        Submit: () => false,
    };

    function transformFormData(data: typeof $form) {
        const hasQuestion = data.question.body.trim().length > 0;
        const isDraft = !existingCase?.id || computedStatus === 'draft';
        const includeQuestion = hasQuestion || !isDraft;
        const reorder: Record<string, number[]> = {};
        if (mediaOrder.length > 0) reorder.media = mediaOrder;
        if (additionalMediaOrder.length > 0) reorder.additional_media = additionalMediaOrder;
        const { question, reorder_media, ...rest } = data;
        const result: Record<string, unknown> = { ...rest };

        // Send S3 keys for completed uploads
        const completedMedia = mediaUploads.filter(u => u.status === 'done' && u.s3Key);
        if (completedMedia.length > 0) {
            result.media = completedMedia.map(u => u.s3Key);
            result.media_captions = completedMedia.map(u => u.caption);
        }

        const completedAdditional = additionalMediaUploads.filter(u => u.status === 'done' && u.s3Key);
        if (completedAdditional.length > 0) {
            result.additional_media = completedAdditional.map(u => u.s3Key);
            result.additional_media_captions = completedAdditional.map(u => u.caption);
        }

        if (logoUpload?.status === 'done' && logoUpload.s3Key) {
            result.institution_logo = logoUpload.s3Key;
        }

        if (includeQuestion) {
            result.question = {
                ...question,
                options: question.type === 'multiple_choice' ? question.options : undefined,
            };
        }
        if (Object.keys(reorder).length > 0) {
            result.reorder_media = reorder;
        }
        return result;
    }

    function navigateToFirstErrorTab() {
        for (const tab of tabs) {
            if (tabErrors[tab]()) {
                setTab(tab);
                return;
            }
        }
    }

    function clearCompletedUploads() {
        mediaUploads = [];
        additionalMediaUploads = [];
        logoUpload = null;
        // Reset order arrays so they re-derive from the refreshed existingMedia prop
        mediaOrder = (existingMedia?.media ?? []).map(m => m.id);
        additionalMediaOrder = (existingMedia?.additional_media ?? []).map(m => m.id);
    }

    const isLastTab = $derived(activeTab === tabs[tabs.length - 1]);

    function advanceTab() {
        const idx = tabs.indexOf(activeTab);
        if (idx < tabs.length - 1) {
            setTab(tabs[idx + 1]);
        }
    }

    function submit(e: SubmitEvent, { advance = false }: { advance?: boolean } = {}) {
        e.preventDefault();
        const currentHash = window.location.hash;
        if (existingCase?.id) {
            $form.transform(transformFormData).patch(`/cases/${existingCase.id}`, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    clearCompletedUploads();
                    if (advance) {
                        advanceTab();
                    } else if (currentHash && window.location.hash !== currentHash) {
                        history.replaceState(null, '', currentHash);
                    }
                },
                onError: () => navigateToFirstErrorTab(),
            });
        } else {
            $form.transform(transformFormData).post('/cases', {
                onSuccess: () => clearCompletedUploads(),
                onError: () => navigateToFirstErrorTab(),
            });
        }
    }

    function addAuthor() {
        $form.authors = [...$form.authors, ''];
    }

    function removeAuthor(index: number) {
        $form.authors = $form.authors.filter((_: string, i: number) => i !== index);
    }

    let authorDragIndex = $state<number | null>(null);
    let authorDragTarget = $state<number | null>(null);

    function reorderAuthor(from: number, to: number) {
        if (from === to) return;
        const authors = [...$form.authors];
        const [moved] = authors.splice(from, 1);
        authors.splice(to, 0, moved);
        $form.authors = authors;
    }

    function addReference() {
        $form.references = [...$form.references, ''];
    }

    function removeReference(index: number) {
        $form.references = $form.references.filter((_: string, i: number) => i !== index);
    }

    function addOption() {
        $form.question.options = [
            ...$form.question.options,
            { body: '', is_correct: false, order: $form.question.options.length },
        ];
    }

    function removeOption(index: number) {
        $form.question.options = $form.question.options.filter((_: QuestionOption, i: number) => i !== index)
            .map((opt: QuestionOption, i: number) => ({ ...opt, order: i }));
    }

    function setCorrectOption(index: number) {
        $form.question.options = $form.question.options.map((opt: QuestionOption, i: number) => ({
            ...opt,
            is_correct: i === index,
        }));
    }

    function setLogoFile(file: File) {
        if (!file.type.startsWith('image/')) return;
        logoPreview = URL.createObjectURL(file);
        const item: UploadItem = {
            id: nextUploadId(),
            file,
            s3Key: null,
            progress: 0,
            status: 'pending',
            caption: '',
        };
        logoUpload = item;
        startUpload(item.id, file);
    }

    function handleLogoChange(e: Event) {
        const input = e.target as HTMLInputElement;
        if (input.files?.[0]) {
            setLogoFile(input.files[0]);
        }
    }

    let logoDragOver = $state(false);

    function handleLogoDrop(e: DragEvent) {
        e.preventDefault();
        logoDragOver = false;
        if (e.dataTransfer?.files?.[0]) {
            setLogoFile(e.dataTransfer.files[0]);
        }
    }

    function removeExistingMedia(id: number) {
        $form.remove_media = [...$form.remove_media, id];
    }

    function submitForReview() {
        if (existingCase?.id) {
            router.post(`/cases/${existingCase.id}/submit`);
        }
    }

    const canSubmitForReview = $derived(
        existingCase?.id && (computedStatus === 'draft' || computedStatus === 'needs_revision')
    );

    // Submit tab — readiness checklist
    function stripHtml(html: string): string {
        const div = document.createElement('div');
        div.innerHTML = html;
        return (div.textContent || div.innerText || '').trim();
    }

    const hasInstitution = $derived($form.institution.trim().length > 0);
    const hasMedia = $derived(
        mediaUploads.some(u => u.status === 'done')
        || (existingMedia?.media ?? []).filter(m => !$form.remove_media.includes(m.id)).length > 0
    );
    const hasQuestion = $derived(stripHtml($form.question.body).length > 0);
    const hasEnoughOptions = $derived(
        $form.question.type !== 'multiple_choice'
        || $form.question.options.filter((o: QuestionOption) => o.body.trim().length > 0).length >= 2
    );
    const hasCorrectOption = $derived(
        $form.question.type !== 'multiple_choice'
        || $form.question.options.some((o: QuestionOption) => o.is_correct)
    );
    const hasCorrectAnswer = $derived(
        $form.question.type !== 'free_text'
        || stripHtml($form.question.correct_answer ?? '').length > 0
    );
    const hasDiscussion = $derived(stripHtml($form.discussion).length > 0);

    interface ChecklistItem {
        label: string;
        passed: boolean;
        required: boolean;
        tab?: Tab;
    }

    const checklist: { get items(): ChecklistItem[] } = {
        get items() {
            return [
                { label: 'Institution is filled in', passed: hasInstitution, required: true, tab: 'Author' as Tab },
                { label: 'At least one case media image', passed: hasMedia, required: true, tab: 'Media' as Tab },
                { label: 'Question has been added', passed: hasQuestion, required: true, tab: 'Question' as Tab },
                ...($form.question.type === 'multiple_choice' ? [
                    { label: 'Multiple choice has at least 2 options', passed: hasEnoughOptions, required: true, tab: 'Question' as Tab },
                    { label: 'A correct answer is selected', passed: hasCorrectOption, required: true, tab: 'Question' as Tab },
                ] : [
                    { label: 'Correct answer is provided', passed: hasCorrectAnswer, required: true, tab: 'Question' as Tab },
                ]),
                { label: 'Discussion is filled in', passed: hasDiscussion, required: true, tab: 'Discussion' as Tab },
                { label: 'At least one reference', passed: $form.references.some((r: string) => r.trim().length > 0), required: false, tab: 'Discussion' as Tab },
            ];
        }
    };

    const hasBlockingIssues = $derived(
        checklist.items.some(item => item.required && !item.passed)
    );

    // Terms acceptance
    let termsNoPatientInfo = $state(false);
    let termsMediaRights = $state(false);
    let termsReviewProcess = $state(false);

    const allTermsAccepted = $derived(termsNoPatientInfo && termsMediaRights && termsReviewProcess);
    const canSubmit = $derived(canSubmitForReview && !hasBlockingIssues && allTermsAccepted);

    // Navigation guard — warn on unsaved changes or uploads in progress
    const hasUnsavedWork = $derived($form.isDirty || uploadsInProgress || mediaUploads.length > 0 || additionalMediaUploads.length > 0 || logoUpload !== null);

    let showLeaveWarning = $state(false);
    let pendingLeaveAction = $state<(() => void) | null>(null);
    let bypassGuard = false;

    function cancelAllUploads() {
        for (const item of mediaUploads) item.abort?.();
        for (const item of additionalMediaUploads) item.abort?.();
        logoUpload?.abort?.();
        mediaUploads = [];
        additionalMediaUploads = [];
        logoUpload = null;
    }

    function confirmLeave() {
        const action = pendingLeaveAction;
        pendingLeaveAction = null;
        cancelAllUploads();
        bypassGuard = true;
        // Navigate first — the modal disappears with the page change.
        // Don't close it beforehand to avoid a fade-out/navigate overlap flicker.
        if (action) action();
    }

    function cancelLeave() {
        showLeaveWarning = false;
        pendingLeaveAction = null;
    }

    onMount(() => {
        const removeBeforeListener = router.on('before', (event) => {
            if (bypassGuard) {
                bypassGuard = false;
                return true;
            }

            // Allow prefetch requests and form submissions
            if ((event.detail.visit as any).prefetch) return true;
            if (event.detail.visit.method !== 'get') return true;

            if (!hasUnsavedWork) return true;

            // Block navigation and show modal
            pendingLeaveAction = () => {
                router.visit(event.detail.visit.url, {
                    method: event.detail.visit.method,
                    preserveState: false,
                });
            };
            showLeaveWarning = true;
            return false;
        });

        function handleBeforeUnload(e: BeforeUnloadEvent) {
            if (hasUnsavedWork) {
                e.preventDefault();
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            removeBeforeListener();
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });
</script>

<svelte:window onhashchange={onHashChange} />

<form onsubmit={(e) => submit(e)}>
    <!-- Tab Navigation: mobile prev/next -->
    <div class="sm:hidden flex items-center justify-between border-b border-gray-700 mb-6 pb-2.5">
        <button
            type="button"
            onclick={() => setTab(tabs[tabs.indexOf(activeTab) - 1])}
            disabled={activeTab === tabs[0]}
            class="flex items-center justify-center size-8 rounded-lg text-gray-500 hover:text-gray-100 hover:bg-gray-800 transition-colors disabled:opacity-25 disabled:pointer-events-none"
            aria-label="Previous tab"
        >
            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"/></svg>
        </button>
        <span class="relative text-sm font-medium text-cyan-400">
            {activeTab}
            {#if tabErrors[activeTab]()}
                <span class="absolute -top-0.5 -right-2.5 h-2 w-2 rounded-full bg-red-500"></span>
            {/if}
        </span>
        <button
            type="button"
            onclick={() => setTab(tabs[tabs.indexOf(activeTab) + 1])}
            disabled={activeTab === tabs[tabs.length - 1]}
            class="flex items-center justify-center size-8 rounded-lg text-gray-500 hover:text-gray-100 hover:bg-gray-800 transition-colors disabled:opacity-25 disabled:pointer-events-none"
            aria-label="Next tab"
        >
            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5-12.5-32.8-12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"/></svg>
        </button>
    </div>

    <!-- Tab Navigation: desktop tabs -->
    <nav bind:this={tabNav} class="relative hidden sm:flex border-b border-gray-700 mb-6">
        {#each tabs as tab (tab)}
            <button
                type="button"
                onclick={() => setTab(tab)}
                data-active={activeTab === tab ? '' : undefined}
                class="relative shrink-0 px-4 py-2.5 text-sm font-medium -mb-px transition-colors {activeTab === tab
                    ? 'text-cyan-400'
                    : 'text-gray-500 hover:text-gray-300'}"
            >
                {tab}
                {#if tabErrors[tab]()}
                    <span class="absolute top-1.5 -right-0.5 h-2 w-2 rounded-full bg-red-500"></span>
                {/if}
            </button>
        {/each}
        <div class="absolute bottom-0 h-0.5 bg-cyan-400 transition-all duration-300 ease-in-out" style="left: {tabIndicatorLeft}px; width: {tabIndicatorWidth}px;"></div>
    </nav>

    <!-- Basics Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Basics'}>
        <fieldset>
            <legend class="block text-sm font-medium text-gray-300 mb-2">Topic *</legend>
            <select
                class="sm:hidden w-full rounded-lg border-2 border-gray-700 bg-gray-900 px-4 py-3 text-sm font-medium text-gray-100 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                value={$form.topic_id ?? ''}
                onchange={(e) => $form.topic_id = e.currentTarget.value ? Number(e.currentTarget.value) : null}
            >
                <option value="">Select a topic…</option>
                {#each topics as topic (topic.id)}
                    <option value={topic.id} disabled={topic.disabled}>{topic.name}{topic.disabled ? ' (disabled)' : ''}</option>
                {/each}
            </select>
            <div class="hidden sm:grid grid-cols-2 gap-3">
                {#each topics as topic (topic.id)}
                    <label class="relative flex rounded-lg border-2 p-4 transition-colors {topic.disabled ? 'cursor-not-allowed border-gray-800 bg-gray-800 opacity-50' : $form.topic_id === topic.id ? 'cursor-pointer border-cyan-500 bg-cyan-950 ring-1 ring-cyan-500' : 'cursor-pointer border-gray-700 bg-gray-900 hover:border-gray-600'}">
                        <input type="radio" name="topic_id" value={topic.id} checked={$form.topic_id === topic.id} onchange={() => $form.topic_id = topic.id} disabled={topic.disabled} class="sr-only" />
                        <span class="text-sm font-medium {topic.disabled ? 'text-gray-400' : $form.topic_id === topic.id ? 'text-cyan-200' : 'text-gray-100'}">{topic.name}{#if topic.disabled} (disabled){/if}</span>
                    </label>
                {/each}
            </div>
            {#if $form.errors.topic_id}<p class="text-red-500 text-sm mt-1">{$form.errors.topic_id}</p>{/if}
        </fieldset>

        <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">History *</label>
            <RichTextEditor
                value={$form.history}
                placeholder="Clinical history..."
                onchange={(html) => $form.history = html}
            />
            {#if $form.errors.history}<p class="text-red-500 text-sm mt-1">{$form.errors.history}</p>{/if}
        </div>

    </div>

    <!-- Author Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Author'}>
        <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Institution</label>
            <div class="flex flex-col sm:flex-row sm:items-start gap-4">
                <div class="w-full sm:w-1/3">
                    {#if logoPreview}
                        <div class="relative group inline-block border border-gray-700 rounded bg-gray-300 p-2">
                            <img src={logoPreview} alt="Institution logo" class="h-14 w-auto object-contain rounded {logoUpload && logoUpload.status !== 'done' ? 'opacity-50' : ''}" />
                            {#if logoUpload && (logoUpload.status === 'uploading' || logoUpload.status === 'pending')}
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <span class="text-xs font-medium text-cyan-400 bg-black/60 rounded px-2 py-0.5">{logoUpload.progress}%</span>
                                </div>
                            {/if}
                            {#if logoUpload?.status === 'error'}
                                <div class="absolute inset-0 flex items-center justify-center bg-black/60">
                                    <button type="button" onclick={() => logoUpload?.retry?.()} class="text-xs bg-red-500 text-white px-2 py-1 rounded">Retry</button>
                                </div>
                            {/if}
                            <button
                                type="button"
                                onclick={() => { logoPreview = null; logoUpload?.abort?.(); logoUpload = null; }}
                                class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 drop-shadow-md"
                            >
                                <svg class="w-2.5 h-2.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".4" fill="currentColor" d="M32 96l384 0-21.4 370.8c-1.5 25.4-22.5 45.2-47.9 45.2l-245.4 0c-25.4 0-46.5-19.8-47.9-45.2L32 96z"/><path fill="currentColor" d="M167.1-16c-13.8 0-26 8.8-30.4 21.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16z"/></svg>
                            </button>
                        </div>
                    {:else}
                        <label
                            class="flex flex-col items-center justify-center gap-1 w-full py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors {logoDragOver ? 'border-cyan-500 text-cyan-400 bg-cyan-950' : 'border-gray-600 text-gray-500 hover:border-cyan-500 hover:text-cyan-400 hover:bg-cyan-950'}"
                            ondragover={(e: DragEvent) => { e.preventDefault(); logoDragOver = true; }}
                            ondragleave={() => logoDragOver = false}
                            ondrop={handleLogoDrop}
                        >
                            <svg class="size-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M0 96C0 60.7 28.7 32 64 32l384 0c35.3 0 64 28.7 64 64l0 320c0 35.3-28.7 64-64 64L64 480c-35.3 0-64-28.7-64-64L0 96zM323.8 202.5c-4.5-6.6-11.9-10.5-19.8-10.5s-15.4 3.9-19.8 10.5l-87 127.6L170.7 297c-4.6-5.7-11.5-9-18.7-9s-14.2 3.3-18.7 9l-64 80c-5.8 7.2-6.9 17.1-2.9 25.4s12.4 13.6 21.6 13.6l96 0 32 0 208 0c8.9 0 17.1-4.9 21.2-12.8s3.6-17.4-1.4-24.7l-120-176zM112 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"/></svg>
                            <span class="text-xs font-medium">Upload logo</span>
                            <input type="file" accept="image/*" onchange={handleLogoChange} class="sr-only" />
                        </label>
                    {/if}
                    {#if $form.errors.institution_logo}<p class="text-red-500 text-sm mt-1">{$form.errors.institution_logo}</p>{/if}
                </div>
                <div class="w-full sm:flex-1">
                    <input
                        bind:value={$form.institution}
                        type="text"
                        class="w-full border border-gray-700 bg-gray-800 text-gray-100 rounded px-3 py-2 text-sm"
                    />
                </div>
            </div>
        </div>

        <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">Authors</label>
            <div class="space-y-2">
                {#each $form.authors as author, i (i)}
                    <div
                        class="flex gap-2 items-center {authorDragTarget === i ? 'border-t-2 border-cyan-500' : ''}"
                        draggable={$form.authors.length > 1}
                        ondragstart={(e: DragEvent) => { authorDragIndex = i; e.dataTransfer!.effectAllowed = 'move'; }}
                        ondragover={(e: DragEvent) => { e.preventDefault(); authorDragTarget = i; }}
                        ondragleave={() => { if (authorDragTarget === i) authorDragTarget = null; }}
                        ondrop={(e: DragEvent) => { e.preventDefault(); reorderAuthor(authorDragIndex!, i); authorDragIndex = null; authorDragTarget = null; }}
                        ondragend={() => { authorDragIndex = null; authorDragTarget = null; }}
                    >
                        {#if $form.authors.length > 1}
                            <div class="flex items-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-300 shrink-0" aria-label="Drag to reorder">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 320 512"><path d="M128 64A64 64 0 1 0 0 64 64 64 0 1 0 128 64zm0 192a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM0 448c0 35.3 28.7 64 64 64s64-28.7 64-64-28.7-64-64-64-64 28.7-64 64zM320 64a64 64 0 1 0 -128 0 64 64 0 1 0 128 0zM192 256a64 64 0 1 0 128 0 64 64 0 1 0 -128 0zM320 448c0-35.3-28.7-64-64-64s-64 28.7-64 64 28.7 64 64 64 64-28.7 64-64z"/></svg>
                            </div>
                        {/if}
                        <input
                            bind:value={$form.authors[i]}
                            type="text"
                            class="flex-1 border border-gray-700 bg-gray-800 text-gray-100 rounded px-3 py-2 text-sm"
                            placeholder="Author name..."
                        />
                        {#if $form.authors.length > 1}
                            <button
                                type="button"
                                onclick={() => removeAuthor(i)}
                                class="px-3 py-2 text-sm text-red-400 hover:text-red-300 border border-red-700 rounded hover:bg-red-950"
                            >
                                Remove
                            </button>
                        {/if}
                    </div>
                    {#if $form.errors[`authors.${i}`]}
                        <p class="text-red-500 text-sm">{$form.errors[`authors.${i}`]}</p>
                    {/if}
                {/each}
            </div>
            <button
                type="button"
                onclick={addAuthor}
                class="mt-2 text-sm text-cyan-400 hover:text-cyan-300"
            >
                + Add Author
            </button>
        </div>
    </div>

    <!-- Media Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Media'}>
        <MediaUploadList
            label="Case Media"
            description="Images, PDFs, or videos related to the case."
            accept="image/*,application/pdf,video/*"
            existingItems={orderedMedia()}
            uploadItems={mediaUploads}
            captions={$form.update_media_captions}
            removedIds={$form.remove_media}
            onfilesadded={(files) => { const items = createUploadItems(files); mediaUploads = [...mediaUploads, ...items]; startUploads(items); }}
            onexistingremove={removeExistingMedia}
            onuploadremove={(id) => { const item = mediaUploads.find(u => u.id === id); item?.abort?.(); mediaUploads = mediaUploads.filter(u => u.id !== id); }}
            onlightbox={openLightbox}
            onexistingreorder={(ids) => { mediaOrder = ids; }}
            onuploadreorder={(items) => { mediaUploads = items; }}
        />
    </div>

    <!-- Question Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Question'}>
        <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Question Text</label>
            <RichTextEditor
                value={$form.question.body}
                placeholder="Enter the question..."
                onchange={(html) => $form.question.body = html}
            />
            {#if $form.errors['question.body']}<p class="text-red-500 text-sm mt-1">{$form.errors['question.body']}</p>{/if}
        </div>

        <fieldset>
            <legend class="block text-sm font-medium text-gray-300 mb-2">Question Type</legend>
            <div class="grid grid-cols-2 gap-3">
                <label class="relative flex cursor-pointer rounded-lg border-2 p-4 transition-colors {$form.question.type === 'free_text' ? 'border-cyan-500 bg-cyan-950 ring-1 ring-cyan-500' : 'border-gray-700 bg-gray-900 hover:border-gray-600'}">
                    <input type="radio" name="question_type" value="free_text" checked={$form.question.type === 'free_text'} onchange={() => $form.question.type = 'free_text'} class="sr-only" />
                    <div class="flex gap-3">
                        <div class="shrink-0 mt-0.5">
                            <svg class="size-6 {$form.question.type === 'free_text' ? 'text-cyan-400' : 'text-gray-400'}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/><path d="M9 7h6"/><path d="M9 11h6"/><path d="M9 15h4"/></svg>
                        </div>
                        <div>
                            <span class="block text-sm font-medium {$form.question.type === 'free_text' ? 'text-cyan-200' : 'text-gray-100'}">Free Text</span>
                            <span class="block text-xs mt-0.5 {$form.question.type === 'free_text' ? 'text-cyan-400' : 'text-gray-500'}">Respondents type their answer. Scored manually by a reviewer.</span>
                        </div>
                    </div>
                </label>
                <label class="relative flex cursor-pointer rounded-lg border-2 p-4 transition-colors {$form.question.type === 'multiple_choice' ? 'border-cyan-500 bg-cyan-950 ring-1 ring-cyan-500' : 'border-gray-700 bg-gray-900 hover:border-gray-600'}">
                    <input type="radio" name="question_type" value="multiple_choice" checked={$form.question.type === 'multiple_choice'} onchange={() => $form.question.type = 'multiple_choice'} class="sr-only" />
                    <div class="flex gap-3">
                        <div class="shrink-0 mt-0.5">
                            <svg class="size-6 {$form.question.type === 'multiple_choice' ? 'text-cyan-400' : 'text-gray-400'}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 5h7"/><path d="M14 16h7"/><path d="M5.5 5.5l1.5 1.5 3-3"/></svg>
                        </div>
                        <div>
                            <span class="block text-sm font-medium {$form.question.type === 'multiple_choice' ? 'text-cyan-200' : 'text-gray-100'}">Multiple Choice</span>
                            <span class="block text-xs mt-0.5 {$form.question.type === 'multiple_choice' ? 'text-cyan-400' : 'text-gray-500'}">Predefined options with one correct answer. Scored automatically.</span>
                        </div>
                    </div>
                </label>
            </div>
        </fieldset>

        {#if $form.question.type === 'free_text'}
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Correct Answer</label>
                <RichTextEditor
                    value={$form.question.correct_answer}
                    placeholder="The expected correct answer..."
                    onchange={(html) => $form.question.correct_answer = html}
                />
                {#if $form.errors['question.correct_answer']}<p class="text-red-500 text-sm mt-1">{$form.errors['question.correct_answer']}</p>{/if}
            </div>
        {/if}

        {#if $form.question.type === 'multiple_choice'}
            {@const correctIndex = $form.question.options.findIndex((o: QuestionOption) => o.is_correct)}
            {@const hasCorrect = correctIndex !== -1}
            <div class="space-y-3">
                <label class="block text-sm font-medium text-gray-300">Options</label>
                {#each $form.question.options as option, i (i)}
                    <div class="flex items-center gap-2">
                        <span class="shrink-0 flex items-center justify-center size-6 rounded-full text-xs font-semibold
                            {hasCorrect
                                ? (option.is_correct ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400')
                                : 'bg-gray-800 text-gray-500'}"
                        >
                            {String.fromCharCode(65 + i)}
                        </span>
                        <input
                            bind:value={$form.question.options[i].body}
                            type="text"
                            class="flex-1 border rounded px-3 py-2 text-sm transition-colors
                                {hasCorrect
                                    ? (option.is_correct ? 'border-green-600 bg-green-950 ring-1 ring-green-600' : 'border-red-700 bg-red-950')
                                    : 'border-gray-700 bg-gray-800 text-gray-100'}"
                            placeholder="Option {String.fromCharCode(65 + i)}..."
                        />
                        {#if $form.question.options.length > 2}
                            <button
                                type="button"
                                onclick={() => removeOption(i)}
                                class="px-2 py-1 text-sm text-red-400 hover:text-red-300"
                            >
                                &times;
                            </button>
                        {/if}
                    </div>
                    {#if $form.errors[`question.options.${i}.body`]}
                        <p class="text-red-500 text-sm ml-8">{$form.errors[`question.options.${i}.body`]}</p>
                    {/if}
                {/each}
                <button
                    type="button"
                    onclick={addOption}
                    class="text-sm text-cyan-400 hover:text-cyan-300"
                >
                    + Add Option
                </button>
                {#if $form.errors['question.options']}<p class="text-red-500 text-sm mt-1">{$form.errors['question.options']}</p>{/if}
            </div>

            <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Correct Answer</label>
                <div class="grid gap-2" style="grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));">
                    {#each $form.question.options as option, i (i)}
                        {@const hasBody = option.body.trim().length > 0}
                        <button
                            type="button"
                            onclick={() => setCorrectOption(i)}
                            disabled={!hasBody}
                            class="flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-colors
                                {option.is_correct
                                    ? 'border-green-500 bg-green-950 ring-1 ring-green-500'
                                    : hasBody
                                        ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                                        : 'border-gray-800 bg-gray-800 opacity-50 cursor-not-allowed'}"
                        >
                            <span class="shrink-0 flex items-center justify-center size-6 rounded-full text-xs font-semibold
                                {option.is_correct ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'}">
                                {#if option.is_correct}
                                    <svg class="size-3.5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/></svg>
                                {:else}
                                    {String.fromCharCode(65 + i)}
                                {/if}
                            </span>
                            <span class="truncate {option.is_correct ? 'font-medium text-green-200' : 'text-gray-300'}">
                                {hasBody ? option.body : `Option ${String.fromCharCode(65 + i)}`}
                            </span>
                        </button>
                    {/each}
                </div>
            </div>
        {/if}

        {#if $form.errors['question']}<p class="text-red-500 text-sm mt-1">{$form.errors['question']}</p>{/if}
    </div>

    <!-- Discussion Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Discussion'}>
        <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Discussion</label>
            <RichTextEditor
                value={$form.discussion}
                onchange={(html) => $form.discussion = html}
            />
        </div>

        <div>
            <label class="block text-sm font-medium text-gray-300 mb-1">Differential Diagnosis</label>
            <RichTextEditor
                value={$form.differential}
                onchange={(html) => $form.differential = html}
            />
        </div>

        <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">References</label>
            <div class="space-y-2">
                {#each $form.references as ref, i (i)}
                    <div class="flex gap-2">
                        <input
                            bind:value={$form.references[i]}
                            type="text"
                            class="flex-1 border border-gray-700 bg-gray-800 text-gray-100 rounded px-3 py-2 text-sm"
                            placeholder="Reference URL or citation..."
                        />
                        <button
                            type="button"
                            onclick={() => removeReference(i)}
                            class="px-3 py-2 text-sm text-red-400 hover:text-red-300 border border-red-700 rounded hover:bg-red-950"
                        >
                            Remove
                        </button>
                    </div>
                {/each}
            </div>
            <button
                type="button"
                onclick={addReference}
                class="mt-2 text-sm text-cyan-400 hover:text-cyan-300"
            >
                + Add Reference
            </button>
        </div>

        <MediaUploadList
            label="Additional Media"
            description="Supplementary files shown after the case is revealed."
            existingItems={orderedAdditionalMedia()}
            uploadItems={additionalMediaUploads}
            captions={$form.update_media_captions}
            removedIds={$form.remove_media}
            onfilesadded={(files) => { const items = createUploadItems(files); additionalMediaUploads = [...additionalMediaUploads, ...items]; startUploads(items); }}
            onexistingremove={removeExistingMedia}
            onuploadremove={(id) => { const item = additionalMediaUploads.find(u => u.id === id); item?.abort?.(); additionalMediaUploads = additionalMediaUploads.filter(u => u.id !== id); }}
            onlightbox={openLightbox}
            onexistingreorder={(ids) => { additionalMediaOrder = ids; }}
            onuploadreorder={(items) => { additionalMediaUploads = items; }}
        />
    </div>

    <!-- Submit Tab -->
    <div class="space-y-6" class:hidden={activeTab !== 'Submit'}>
        {#if !canSubmitForReview}
            <div class="rounded-md bg-gray-800 border border-gray-700 p-4 text-sm text-gray-500">
                {#if !existingCase?.id}
                    Save your case first before submitting for review.
                {:else}
                    This case is no longer in draft status and cannot be resubmitted.
                {/if}
            </div>
        {:else}
            <!-- Readiness Checklist -->
            <div>
                <h3 class="text-sm font-medium text-gray-100 mb-3">Readiness checklist</h3>
                <ul class="space-y-2">
                    {#each checklist.items as item (item.label)}
                        <li class="flex items-start gap-2 text-sm">
                            {#if item.passed}
                                <svg class="size-5 shrink-0 text-green-500 mt-px" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                            {:else if item.required}
                                <svg class="size-5 shrink-0 mt-px text-red-400" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd"/></svg>
                            {:else}
                                <svg class="size-5 shrink-0 mt-px text-amber-400" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/></svg>
                            {/if}
                            <span class="{item.passed ? 'text-gray-300' : item.required ? 'text-red-400' : 'text-gray-400'}">
                                {item.label}
                                {#if !item.passed && !item.required}
                                    <span class="text-gray-400">(optional)</span>
                                {/if}
                                {#if !item.passed && item.tab}
                                    <button type="button" onclick={() => setTab(item.tab!)} class="ml-1 text-cyan-400 hover:underline">Go to {item.tab}</button>
                                {/if}
                            </span>
                        </li>
                    {/each}
                </ul>
            </div>

            <!-- Terms -->
            <div class="border-t border-gray-700 pt-6">
                <h3 class="text-sm font-medium text-gray-100 mb-3">Terms</h3>
                <div class="space-y-3">
                    <label class="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                        <input type="checkbox" bind:checked={termsNoPatientInfo} class="mt-0.5 rounded border-gray-600 text-cyan-600 focus:ring-cyan-500" />
                        I confirm this case does not contain any patient-identifiable information.
                    </label>
                    <label class="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                        <input type="checkbox" bind:checked={termsMediaRights} class="mt-0.5 rounded border-gray-600 text-cyan-600 focus:ring-cyan-500" />
                        I have the rights to share the media included in this case.
                    </label>
                    <label class="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
                        <input type="checkbox" bind:checked={termsReviewProcess} class="mt-0.5 rounded border-gray-600 text-cyan-600 focus:ring-cyan-500" />
                        I understand this case will be reviewed by an administrator before publication.
                    </label>
                </div>
            </div>

            <!-- What happens next -->
            <div class="border-t border-gray-700 pt-6">
                <h3 class="text-sm font-medium text-gray-100 mb-2">What happens next</h3>
                <ul class="text-sm text-gray-500 space-y-1 list-disc ml-5">
                    <li>Your case will be reviewed by an admin.</li>
                    <li>You won't be able to edit it until the review is complete.</li>
                    <li>You'll be notified when it's approved or if changes are requested.</li>
                </ul>
            </div>

        {/if}
    </div>

    <!-- Actions (always visible) -->
    <div class="flex items-center gap-3 mt-8 pt-6 border-t border-gray-700">
        {#if existingCase && !isLastTab}
            <button
                type="button"
                onclick={(e) => submit(new SubmitEvent('submit', { cancelable: true }), { advance: true })}
                disabled={$form.processing || uploadsInProgress}
                class="bg-cyan-600 text-white px-4 py-2 rounded text-sm hover:bg-cyan-500 disabled:opacity-50 inline-flex items-center gap-2"
            >
                {#if uploadsInProgress}
                    <svg class="size-4 animate-spin" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" opacity="0.25" />
                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-dasharray={2 * Math.PI * 8} stroke-dashoffset={2 * Math.PI * 8 * 0.75} stroke-linecap="round" />
                    </svg>
                    Uploading files…
                {:else if $form.processing}
                    Saving…
                {:else}
                    Save & Continue
                {/if}
            </button>
            <button
                type="submit"
                disabled={$form.processing || uploadsInProgress}
                class="text-gray-400 hover:text-gray-200 px-4 py-2 rounded text-sm disabled:opacity-50 transition-colors"
            >
                Save
            </button>
        {:else if existingCase && isLastTab}
            {#if canSubmitForReview}
                <button
                    type="button"
                    onclick={submitForReview}
                    disabled={!canSubmit || $form.processing}
                    class="bg-green-600 text-white px-5 py-2.5 rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Submit for Review
                </button>
            {/if}
            {#if $form.isDirty || uploadsInProgress}
                <button
                    type="submit"
                    disabled={$form.processing || uploadsInProgress}
                    class="bg-cyan-600 text-white px-4 py-2 rounded text-sm hover:bg-cyan-500 disabled:opacity-50 inline-flex items-center gap-2"
                >
                    {#if uploadsInProgress}
                        <svg class="size-4 animate-spin" viewBox="0 0 20 20">
                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" opacity="0.25" />
                            <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-dasharray={2 * Math.PI * 8} stroke-dashoffset={2 * Math.PI * 8 * 0.75} stroke-linecap="round" />
                        </svg>
                        Uploading files…
                    {:else if $form.processing}
                        Saving…
                    {:else}
                        Save Changes
                    {/if}
                </button>
            {/if}
        {:else}
            <button
                type="submit"
                disabled={$form.processing || uploadsInProgress}
                class="bg-cyan-600 text-white px-4 py-2 rounded text-sm hover:bg-cyan-500 disabled:opacity-50 inline-flex items-center gap-2"
            >
                {#if uploadsInProgress}
                    <svg class="size-4 animate-spin" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" opacity="0.25" />
                        <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="2.5" stroke-dasharray={2 * Math.PI * 8} stroke-dashoffset={2 * Math.PI * 8 * 0.75} stroke-linecap="round" />
                    </svg>
                    Uploading files…
                {:else if $form.processing}
                    Saving…
                {:else}
                    Create a Draft
                {/if}
            </button>
        {/if}
    </div>
</form>

<MediaLightbox
    images={lightboxImages}
    bind:index={lightboxIndex}
    bind:open={lightboxOpen}
    onclose={closeLightbox}
    oncaptionchange={handleLightboxCaptionChange}
/>

<Modal bind:open={showLeaveWarning} title="Unsaved Changes" panelClass="leave-warning-glow border border-amber-500/60">
    <p class="text-sm text-gray-400 mb-2">
        {#if uploadsInProgress}
            You have file uploads in progress. Leaving now will cancel them.
        {:else}
            You have unsaved changes that will be lost if you leave this page.
        {/if}
    </p>
    <div class="flex justify-end gap-3 mt-5">
        <button
            type="button"
            onclick={cancelLeave}
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-gray-700"
        >
            Stay on page
        </button>
        <button
            type="button"
            onclick={confirmLeave}
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-500"
        >
            Leave page
        </button>
    </div>
</Modal>

<style>
    @keyframes leave-warning-glow {
        0%, 100% {
            box-shadow: 0 0 40px 20px rgba(245, 158, 11, 0.3);
        }
        50% {
            box-shadow: 0 0 60px 30px rgba(245, 158, 11, 0.6);
        }
    }

    :global(.leave-warning-glow) {
        animation: leave-warning-glow 2s ease-in-out infinite;
    }
</style>
